import { useEffect, useMemo, useState } from 'react';
import { Check, Eye, Loader2, MoveRight, Send, Square, X } from 'lucide-react';

import type { TtqModelInvocationOptions } from '@kelilearning/psycheflow-contracts';

import { TtqAngleRelationIcon } from './TtqAngleRelationIcon';
import {
  TTQ_QUESTION_CHAIN_VARIANTS,
  type TtqQuestionChainVariantId,
} from './ttqBehaviorCatalog';
import {
  TTQ_BOTTOM_PANEL_L1_WIDTH_PX,
  TTQ_BOTTOM_PANEL_L2_WIDTH_PX,
  TTQ_BOTTOM_PANEL_SHELL_CLASS,
  TTQ_BOTTOM_PANEL_SHELL_STYLE,
} from './ttqBottomPanelShell';
import { TtqWorkbenchModelIconLocked, TtqWorkbenchModelMenu } from './TtqWorkbenchModelMenu';
import {
  getTtqWorkbenchModelOption,
  readStoredTtqWorkbenchModelId,
  toTtqModelInvocation,
} from './ttqWorkbenchModels';
import {
  formatTtqWorkbenchError,
  isTtqDirectionPreviewRetryable,
} from './ttqWorkbenchErrors';

async function previewDirectionWithRetry<T>(
  run: () => Promise<T>,
): Promise<T> {
  try {
    return await run();
  } catch (firstError) {
    if (!isTtqDirectionPreviewRetryable(firstError)) throw firstError;
    await new Promise((resolve) => {
      window.setTimeout(resolve, 700);
    });
    return run();
  }
}

export type Ttq1qnqSegmentItem = {
  objectId: string;
  particleId: string;
  text: string;
};

export type Ttq1qnqDirectionPreview = {
  directionPreview: string;
};

type TtqBottomPanel1qnqProps = {
  visible: boolean;
  items: Ttq1qnqSegmentItem[];
  /** 无选段时的整题源颗粒（菜单直开一题多题） */
  sourceParticleId?: string | null;
  submitting?: boolean;
  previewing?: boolean;
  error?: string | null;
  /** 调试/排障用完整错误文本（含模型原文预览等） */
  errorDetail?: string | null;
  onClear: () => void;
  onSubmit: (payload: {
    description: string;
    items: Ttq1qnqSegmentItem[];
    questionChainVariantId: TtqQuestionChainVariantId;
    sourceParticleId?: string;
    /** 仅在用户主动点过「变式方向预览」时传入 */
    directionPreview?: string;
    model?: TtqModelInvocationOptions;
  }) => void;
  onPreviewDirection: (payload: {
    description: string;
    items: Ttq1qnqSegmentItem[];
    questionChainVariantId: TtqQuestionChainVariantId;
    sourceParticleId?: string;
    model?: TtqModelInvocationOptions;
  }) => Promise<Ttq1qnqDirectionPreview>;
};

const L1_DESCRIPTION = '从源题出发改变题面/条件/情境，一对多衍生多道不同题目；含全部 GO-ON 子变式。';

const VARIANT_GUIDANCE: Record<TtqQuestionChainVariantId, string> = {
  question_chain: '在同一题干下层层递进追问，生成一道关联新题。',
  condition_generalize:
    '将原题条件一般化（参数化/抽象化）。必须保留原题场景与题型骨架，只放宽/抽象被指定的条件；禁止换成无关题材另写新题。',
  condition_specialize:
    '将原题条件特殊化（替换/收窄指定条件）。必须保留原题场景、题型与叙事骨架，只改被指定的条件及由此必然引起的数值/必败点等；禁止换成无关题材另写新题。',
  conclusion_progression: '在原题结论基础上递进，生成一道新题。',
  inverse_swap: '交换原题中条件与结论角色，生成逆向变式题。',
  context_transfer: '保持数学结构，迁移到不同情境，生成一道新题。',
  element_weak_strong: '改变原题中某一要素的强弱（如范围、数量级），生成变式题。',
  depth_extend: '在原题基础上加深一层（综合、拓展），生成新题。',
  error_boundary: '围绕原题易错边界构造陷阱或辨析题。',
  custom: '按用户自定义说明构造变式题。',
};

const VARIANT_HARD_CONSTRAINTS: Partial<Record<TtqQuestionChainVariantId, string>> = {
  condition_specialize: [
    '【条件特殊化 · 硬约束】',
    '- 保留原题场景/题型/叙事，禁止题材置换或另起炉灶写新题。',
    '- 只改用户指定或【题面锚点】选中的条件；未选中内容原样保留。',
  ].join('\n'),
  condition_generalize: [
    '【条件一般化 · 硬约束】',
    '- 保留原题场景/题型/叙事骨架，禁止题材置换或另起炉灶写新题。',
    '- 只一般化用户指定或【题面锚点】选中的条件；未选中内容原样保留。',
  ].join('\n'),
};

function formatExplosionAnchorText(items: Array<{ text: string }>): string {
  return items
    .map((item) => String(item.text || '').trim())
    .filter(Boolean)
    .map((text, index) => `${index + 1}. ${text}`)
    .join('\n');
}

function buildLocalPromptPreview(input: {
  variantId: TtqQuestionChainVariantId;
  variantLabel: string;
  anchorText: string;
  customPrompt: string;
  directionPreview?: string;
  wholeMode: boolean;
}) {
  const hasAnchor = Boolean(input.anchorText.trim()) && !input.wholeMode;
  const preserveScene =
    input.variantId === 'condition_specialize' || input.variantId === 'condition_generalize';
  const hard = VARIANT_HARD_CONSTRAINTS[input.variantId];
  return [
    '【任务】一题多题变式（前端约束预览 · 非最终送模全文）',
    '说明：完整【题干】/【源题参考解答】由后端按源颗粒组装后写入实际 messages；下方约束与锚点/方向会随请求送出。',
    `【变式类型】${input.variantLabel}（${input.variantId}）`,
    `【变式策略】${VARIANT_GUIDANCE[input.variantId]}`,
    hard || '',
    hasAnchor
      ? [
        '【模式】文本段变式（必须遵守）',
        preserveScene
          ? '硬约束：变式只能围绕下方【题面锚点】选段展开；未选中的题面条件默认保持原状，禁止整题自由改写或换题材。'
          : '硬约束：变式只能围绕下方【题面锚点】选段展开；未选中的题面条件默认保持原状，禁止整题自由改写。',
        `【题面锚点】\n${input.anchorText}`,
      ].join('\n')
      : preserveScene
        ? '【模式】整题拓展：在原题场景与题型骨架内落实所选变式策略；禁止另起炉灶写无关新题。'
        : '【模式】整题拓展：可对整题结构做变式，但仍须落实所选变式策略。',
    input.directionPreview ? `【变式方向预览】\n${input.directionPreview}` : '',
    input.customPrompt ? `【补充说明】${input.customPrompt}` : '',
  ].filter(Boolean).join('\n');
}

/** 文本段采集或整题发起一题多题：L1 同款菜单块（不可点）+ 可选 L2 + 采集盘 + 方向预览 */
export function TtqBottomPanel1qnq({
  visible,
  items,
  sourceParticleId = null,
  submitting = false,
  previewing = false,
  error = null,
  errorDetail = null,
  onClear,
  onSubmit,
  onPreviewDirection,
}: TtqBottomPanel1qnqProps) {
  const [description, setDescription] = useState('');
  const [variantId, setVariantId] = useState<TtqQuestionChainVariantId | null>(null);
  const [directionPreview, setDirectionPreview] = useState<Ttq1qnqDirectionPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewErrorDetail, setPreviewErrorDetail] = useState<string | null>(null);
  const [localPreviewing, setLocalPreviewing] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const displayErrorDetail = previewErrorDetail || errorDetail;
  const [modelId, setModelId] = useState(() => readStoredTtqWorkbenchModelId());
  const count = items.length;
  const wholeMode = count === 0 && Boolean(sourceParticleId);
  const busy = submitting || previewing || localPreviewing;
  const selectedModel = useMemo(
    () => toTtqModelInvocation(getTtqWorkbenchModelOption(modelId)),
    [modelId],
  );

  useEffect(() => {
    if (!visible) {
      setDescription('');
      setVariantId(null);
      setDirectionPreview(null);
      setPreviewError(null);
      setPreviewErrorDetail(null);
      return;
    }
    setModelId(readStoredTtqWorkbenchModelId());
  }, [visible]);

  const itemsKey = useMemo(
    () => items.map((item) => `${item.objectId}:${item.text}`).join('|'),
    [items],
  );

  useEffect(() => {
    setDirectionPreview(null);
    setPreviewError(null);
    setPreviewErrorDetail(null);
  }, [variantId, itemsKey, description, sourceParticleId]);

  const selectedVariant = useMemo(
    () => TTQ_QUESTION_CHAIN_VARIANTS.find((item) => item.id === variantId) || null,
    [variantId],
  );

  const anchorText = useMemo(() => formatExplosionAnchorText(items), [items]);

  const promptPreviewText = useMemo(() => {
    if (!variantId || !selectedVariant) return '';
    return buildLocalPromptPreview({
      variantId,
      variantLabel: selectedVariant.label,
      anchorText,
      customPrompt: description.trim(),
      directionPreview: directionPreview?.directionPreview?.trim() || undefined,
      wholeMode,
    });
  }, [anchorText, description, directionPreview, selectedVariant, variantId, wholeMode]);

  if (!visible || (!count && !wholeMode)) return null;

  const hasDirectionPreview = Boolean(directionPreview?.directionPreview?.trim());
  const canSubmit = Boolean(variantId) && !busy && hasDirectionPreview
    && (variantId !== 'custom' || description.trim().length > 0);

  // 优先会话当前源题；选段模式以 tray 共同颗粒为准（见 Session 侧单源约束）
  const resolvedSourceId = sourceParticleId || items[0]?.particleId || undefined;

  return (
    <div className={TTQ_BOTTOM_PANEL_SHELL_CLASS} style={TTQ_BOTTOM_PANEL_SHELL_STYLE}>
      <div className="flex items-center justify-between border-b border-border px-4 py-2">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-semibold text-ink">
            {wholeMode ? '一题多题 · 整题拓展' : '一题多题 · 文本段采集'}
          </span>
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
            {wholeMode ? '整题' : `已采 ${count} 段`}
          </span>
        </div>
        <button
          type="button"
          onClick={() => {
            setDescription('');
            setVariantId(null);
            setDirectionPreview(null);
            setPreviewError(null);
            setPreviewErrorDetail(null);
            onClear();
          }}
          className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-muted hover:bg-black/[0.05]"
        >
          <X size={14} />
          清空
        </button>
      </div>

      <div className="max-h-24 space-y-1 overflow-y-auto border-b border-border px-4 py-2">
        {wholeMode ? (
          <p className="text-xs font-medium text-ink/80">未选文本段 · 将以整题作为变式出发点</p>
        ) : (
          items.map((item, index) => (
            <p key={item.objectId} className="line-clamp-2 text-sm font-semibold leading-snug text-ink">
              <span className="mr-1.5 text-primary">{index + 1}.</span>
              {item.text}
            </p>
          ))
        )}
      </div>

      <div className="overflow-x-auto overscroll-x-contain px-3 py-2 [-webkit-overflow-scrolling:touch]">
        {count > 0 && !variantId ? (
          <p className="mb-2 rounded-md border border-amber-300/80 bg-amber-50 px-2.5 py-1.5 text-[11px] font-medium text-amber-800">
            请至少选择一个「一题多题」分项（L2 变式）后再发送
          </p>
        ) : null}
        <div className="flex w-full max-w-full flex-nowrap items-stretch justify-start gap-1.5">
          <div
            className="flex shrink-0 cursor-not-allowed flex-col items-start px-1 py-1 text-left text-muted"
            style={{ width: TTQ_BOTTOM_PANEL_L1_WIDTH_PX }}
            aria-disabled
            title="一题多题已锁定，请选择 L2 变式"
          >
            <div className="flex w-full items-start justify-between gap-2">
              <TtqAngleRelationIcon angleId="question_chain" size="sm" muted />
              <span className="shrink-0 text-[10px] font-medium text-muted">一对多</span>
            </div>
            <p className="mt-1 line-clamp-2 text-xs text-muted">{L1_DESCRIPTION}</p>
            <span className="mt-1 text-[10px] text-muted">不可选 · 已锁定</span>
          </div>

          <div
            className="grid grid-flow-row grid-rows-2 gap-1.5"
            style={{
              gridTemplateColumns: `repeat(${Math.ceil(TTQ_QUESTION_CHAIN_VARIANTS.length / 2)}, ${TTQ_BOTTOM_PANEL_L2_WIDTH_PX}px)`,
            }}
          >
            {TTQ_QUESTION_CHAIN_VARIANTS.map((item) => {
              const selected = variantId === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  disabled={busy}
                  onClick={() => setVariantId(item.id)}
                  className={
                    'flex h-[52px] flex-col justify-center rounded-md border px-2.5 py-1.5 text-left transition-colors '
                    + (selected
                      ? 'border-primary bg-primary/10 ring-1 ring-primary/30'
                      : 'border-primary/30 bg-primary/5 hover:bg-primary/10')
                  }
                  style={{ width: TTQ_BOTTOM_PANEL_L2_WIDTH_PX }}
                >
                  <div className="flex w-full items-center justify-between gap-1">
                    <span className="truncate text-[12px] font-medium leading-none text-ink">{item.label}</span>
                    <span className="shrink-0 text-[9px] font-medium text-muted">L2</span>
                  </div>
                  <p className="mt-0.5 line-clamp-1 text-[10px] leading-tight text-muted">{item.description}</p>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="border-t border-border px-4 py-3">
        {promptPreviewText ? (
          <div className="mb-3">
            <p className="mb-1.5 text-[11px] font-medium text-muted">提示词预览</p>
            <pre className="max-h-24 overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-canvas/60 px-3 py-2 text-[11px] leading-relaxed text-ink/80">
              {promptPreviewText}
            </pre>
          </div>
        ) : null}

        {variantId ? (
          <div className="mb-3">
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <p className="text-[11px] font-medium text-muted">变式方向预览</p>
              <div className="flex items-center gap-1.5">
                <TtqWorkbenchModelIconLocked modelId={modelId} />
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    void (async () => {
                      setLocalPreviewing(true);
                      setPreviewError(null);
                      setPreviewErrorDetail(null);
                      try {
                        const result = await previewDirectionWithRetry(() => onPreviewDirection({
                          description: description.trim(),
                          items,
                          questionChainVariantId: variantId,
                          sourceParticleId: resolvedSourceId,
                          model: selectedModel,
                        }));
                        setDirectionPreview(result);
                      } catch (previewErr) {
                        setDirectionPreview(null);
                        const formatted = formatTtqWorkbenchError(previewErr);
                        setPreviewError(formatted.summary);
                        setPreviewErrorDetail(formatted.detail);
                      } finally {
                        setLocalPreviewing(false);
                      }
                    })();
                  }}
                  className={
                    'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium '
                    + (busy
                      ? 'cursor-not-allowed border-border text-muted'
                      : 'border-primary/35 bg-primary/10 text-primary hover:bg-primary/15')
                  }
                >
                  {localPreviewing || previewing
                    ? <Loader2 size={12} className="animate-spin" />
                    : <Eye size={12} />}
                  变式方向预览
                </button>
              </div>
            </div>
            {previewError ? (
              <div className="mb-1.5 rounded-lg border border-danger/30 bg-danger/5 px-3 py-2">
                <p className="text-xs leading-relaxed text-danger">{previewError}</p>
                {previewErrorDetail ? (
                  <button
                    type="button"
                    className="mt-1.5 inline-flex items-center gap-1 rounded border border-danger/35 px-2 py-0.5 text-[11px] font-medium text-danger hover:bg-danger/10"
                    onClick={() => setDetailOpen(true)}
                  >
                    查看错误详情
                  </button>
                ) : null}
              </div>
            ) : null}
            {directionPreview ? (
              <div className="rounded-lg border border-primary/20 bg-primary/[0.04] px-3 py-2 text-xs text-ink">
                <p className="whitespace-pre-wrap leading-relaxed">{directionPreview.directionPreview}</p>
                <p className="mt-2 text-[10px] text-muted">已纳入后续发送 · 改动选段 / 变式 / 说明后需重新预览</p>
              </div>
            ) : (
              <p className="text-[11px] font-medium text-amber-800">
                须先点「变式方向预览」后才能发送给大模型
              </p>
            )}
          </div>
        ) : null}

        <label className="mb-1.5 block text-[11px] font-medium text-muted">
          衍生说明{variantId === 'custom' ? '（自定义必填）' : '（可选）'}
        </label>
        <textarea
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          rows={2}
          placeholder={wholeMode ? '说明希望如何整题一题多题衍生…' : '针对已采文本段，说明希望如何一题多题衍生…'}
          className="w-full resize-none rounded-lg border border-border bg-canvas/60 px-3 py-2 text-sm text-ink outline-none focus:border-primary/40"
          disabled={busy}
        />
        {!previewError && error ? (
          <div className="mt-1.5 rounded-lg border border-danger/30 bg-danger/5 px-3 py-2">
            <p className="text-xs leading-relaxed text-danger">{error}</p>
            {errorDetail ? (
              <button
                type="button"
                className="mt-1.5 inline-flex items-center gap-1 rounded border border-danger/35 px-2 py-0.5 text-[11px] font-medium text-danger hover:bg-danger/10"
                onClick={() => setDetailOpen(true)}
              >
                查看错误详情
              </button>
            ) : null}
          </div>
        ) : null}
        <div className="mt-2 flex items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-muted">
            <span className="font-medium text-ink/70">图例</span>
            <span className="inline-flex items-center gap-1"><Square size={12} />题</span>
            <span className="inline-flex items-center gap-1"><Check size={12} />解</span>
            <span className="inline-flex items-center gap-1"><MoveRight size={12} />变式</span>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <TtqWorkbenchModelMenu
              value={modelId}
              disabled={busy}
              onChange={(option) => setModelId(option.id)}
            />
            <button
              type="button"
              disabled={!canSubmit}
              title={!hasDirectionPreview ? '请先完成变式方向预览' : undefined}
              onClick={() => {
                if (!variantId || !hasDirectionPreview) return;
                onSubmit({
                  description: description.trim(),
                  items,
                  questionChainVariantId: variantId,
                  sourceParticleId: resolvedSourceId,
                  directionPreview: directionPreview?.directionPreview?.trim() || undefined,
                  model: selectedModel,
                });
              }}
              className={
                'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium '
                + (submitting || canSubmit
                  ? 'bg-primary text-white hover:bg-primary/90 disabled:opacity-90'
                  : 'cursor-not-allowed bg-black/[0.04] text-muted')
              }
            >
              {submitting
                ? <Loader2 size={14} className="animate-spin text-white" />
                : <Send size={14} />}
              {submitting ? '生成中…' : '发送给大模型'}
            </button>
          </div>
        </div>
      </div>

      {detailOpen && displayErrorDetail ? (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/45 p-4">
          <div className="flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-xl">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <p className="text-sm font-semibold text-ink">错误详情</p>
              <button type="button" className="text-muted hover:text-ink" onClick={() => setDetailOpen(false)}>
                <X size={16} />
              </button>
            </div>
            <pre className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap px-4 py-3 text-[11px] leading-relaxed text-ink">
              {displayErrorDetail}
            </pre>
            <div className="flex justify-end gap-2 border-t border-border px-4 py-3">
              <button
                type="button"
                className="rounded-md border border-border px-3 py-1.5 text-xs text-muted hover:bg-black/[0.03]"
                onClick={() => setDetailOpen(false)}
              >
                关闭
              </button>
              <button
                type="button"
                className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white"
                onClick={() => {
                  void navigator.clipboard?.writeText(displayErrorDetail);
                }}
              >
                复制详情
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
